import React, { useMemo, useState } from 'react'

import { useTranslation, Trans } from 'next-i18next'
import { useRouter } from 'next/router'

import { AuthError, EnrollmentsFields } from '@dentalux/security'

import { ParsedUrlQuery } from 'querystring'
import AuthLayout from 'src/components/AuthLayout'
import LinkText from 'src/components/LinkText/LinkText'
import usePrepareErrorMessage from 'src/hooks/usePrepareErrorMessage'

import { Notice } from './components/Notice/Notice'
import RegistrationForm from './components/RegistrationForm'
import * as S from './Registration.styles'

type Props = {
  onSubmit: (data: EnrollmentsFields) => void
  processing?: boolean
  error?: AuthError
}

const safeGet = (query: ParsedUrlQuery, key: string): string => {
  return (query[key] as string) || ''
}

const getSource = (query: ParsedUrlQuery): string => {
  return ((query?.patientRegistrationSource || query?.['utm_campaign']) as string) || 'NOT_SET'
}

export const RegistrationScreen = ({ onSubmit, processing, error }: Props) => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const errorMessage = usePrepareErrorMessage(error)
  const [usedEmail, setUsedEmail] = useState<string>('')

  const urlValues = useMemo(() => ({ email: safeGet(query, 'email') }), [query])

  const handleSubmit = (data: EnrollmentsFields) => {
    onSubmit({
      ...data,
      patientRegistrationSource: getSource(query),
    })
    if (!error) {
      setUsedEmail(data.email)
    }
  }

  if (usedEmail) {
    return (
      <AuthLayout
        heading={t('b2c.registration.sentHeading')}
        subHeading={
          <>
            <Trans
              i18nKey="b2c.registration.sentSubheading"
              values={{ email: usedEmail }}
              components={[<strong key={0} />]}
            />
            <S.ChangeEmail onClick={() => setUsedEmail('')} variant="borderless">
              {t('b2c.registration.changeEmail')}
            </S.ChangeEmail>
          </>
        }
      >
        <S.MessageSent />
        <Notice />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      heading={t('b2c.registration.heading')}
      subHeading={
        <Trans i18nKey="b2c.registration.subheading" components={[<LinkText key="login-link" href="/login" />]} />
      }
      error={errorMessage}
    >
      <RegistrationForm initialValues={urlValues} onSubmit={handleSubmit} isLoading={processing} />
      <Notice />
    </AuthLayout>
  )
}
